import React, { useState } from "react";
import "./Header.css";
import { Button, Menu, Dropdown } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HomeOutlined,
  SlackOutlined,
  SoundOutlined,
  ContactsOutlined,
  InfoCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import Logo from "./logo/Logo";
import Login from "../login/Login";
import { useSelector, useDispatch } from "react-redux";
import { logout as logoutAction } from "../../redux/slices/userSlice";
import { clearAuthStorage } from "../../utilities/auth";
import { HEADER_LABELS, HEADER_DROPDOWN } from "../../utilities/localization";
import { OUR_CONTACT_INFO } from "../../utilities/constant";
import { useStatus } from "../../context/StatusContext";
import PATHS from "../../routes/path";
import discordLogo from "../../images/discord-icon.png";

function Header() {
  const [loginVisible, setLoginVisible] = useState(false);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s) => s.user.isAuthenticated);
  const username = useSelector((s) => s.user.user?.username);
  const role = useSelector((s) => s.user.role);
  const isAdmin = role === "admin";
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname || PATHS.HOME;
  const { showStatus } = useStatus();

  const handleLogout = () => {
    clearAuthStorage();
    dispatch(logoutAction());
    showStatus({ type: "success", content: HEADER_DROPDOWN.LOGOUT });
    navigate(PATHS.HOME);
  };

  const profileMenuItems = [
    {
      key: "username",
      label: (
        <div
          style={{ paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}
        >
          <strong>{username || HEADER_DROPDOWN.DEFAULT_USER}</strong>
        </div>
      ),
      disabled: true,
    },
    {
      key: "role",
      label: (
        <div style={{ paddingTop: "8px", paddingBottom: "12px" }}>
          <span style={{ fontSize: "12px", color: "#999" }}>
            {HEADER_DROPDOWN.ROLE_LABEL}{" "}
            <strong style={{ color: "#000", textTransform: "capitalize" }}>
              {role}
            </strong>
          </span>
        </div>
      ),
      disabled: true,
    },
    ...(isAuthenticated && isAdmin
      ? [
          {
            key: "admin-users",
            label: (
              <Link to={PATHS.USER_MANAGEMENT} className="admin-panel-link">
                <TeamOutlined /> Manage Users
              </Link>
            ),
          },
        ]
      : []),
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: HEADER_DROPDOWN.LOGOUT,
      onClick: handleLogout,
    },
  ];

  const items = [
    {
      key: PATHS.HOME,
      label: (
        <Link to={PATHS.HOME}>
          <HomeOutlined /> {HEADER_LABELS.HOME}
        </Link>
      ),
    },
    {
      key: PATHS.SERVICES,
      label: (
        <Link to={PATHS.SERVICES}>
          <SlackOutlined /> {HEADER_LABELS.SERVICES}
        </Link>
      ),
      disabled: true, // Services page is coming soon
    },
    {
      key: PATHS.ANNOUNCEMENTS,
      label: (
        <Link to={PATHS.ANNOUNCEMENTS}>
          <SoundOutlined /> {HEADER_LABELS.ANNOUNCEMENTS}
        </Link>
      ),
    },
    {
      key: PATHS.ABOUT,
      label: (
        <Link to={PATHS.ABOUT}>
          <InfoCircleOutlined /> {HEADER_LABELS.ABOUT}
        </Link>
      ),
    },
    {
      key: PATHS.CONTACT,
      label: (
        <Link to={PATHS.CONTACT}>
          <ContactsOutlined /> {HEADER_LABELS.CONTACT}
        </Link>
      ),
    },
  ];

  const selectedKey = items.find((item) => path.startsWith(item.key))?.key;

  return (
    <header className="site-header">
      <div className="header-inner">
        <Link to={PATHS.HOME} className="logo-link">
          <Logo />
        </Link>
        <div className="header-navbar">
          <Menu mode="horizontal" selectedKeys={[selectedKey]} items={items} />
        </div>
        <div className="discord-link-wrapper">
          <a
            href={OUR_CONTACT_INFO.DISCORD_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="discord-link"
          >
            <img src={discordLogo} alt="Discord" className="discord-icon-header" />
            <span>Join Discord</span>
          </a>
        </div>
        <div>
          {isAuthenticated ? (
            <Dropdown
              menu={{ items: profileMenuItems }}
              trigger={["click"]}
              getPopupContainer={(triggerNode) =>
                triggerNode?.parentElement || document.body
              }
              // overlayClassName="profile-dropdown-menu"
            >
              <Button icon={<UserOutlined />} className="join-discord-btn">
                {username || "User"}
              </Button>
            </Dropdown>
          ) : (
            <>
              <Button
                icon={<UserOutlined />}
                className="join-discord-btn"
                onClick={() => setLoginVisible(true)}
              >
                {HEADER_LABELS.LOGIN}
              </Button>
              <Login
                visible={loginVisible}
                onClose={() => setLoginVisible(false)}
                mode={"signin"}
              />
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default React.memo(Header);
